import {
  Alert,
  Button,
  Checkbox,
  Drawer,
  Form,
  Input,
  Popconfirm,
  Select,
  Space,
  Switch,
  Tag,
  Typography,
  notification,
} from "antd";
import { useEffect, useState } from "react";
import useApi from "../../utils/useApi";
import ConversationModal from "../ai-agent/ConversationModal";
import { formatarTelefone, motivoParaNaoExcluir } from "../ai-agent/textos";
import { describeError, formatDateTime } from "../ai-reports/status";

const { Text, Title } = Typography;

const SECAO = {
  padding: "16px 0 4px",
  borderBottom: "1px solid #f0f0f0",
};

function Secao({ titulo, children, ultima }) {
  return (
    <section style={ultima ? { ...SECAO, borderBottom: 0 } : SECAO}>
      <Title level={5} style={{ marginTop: 0, marginBottom: 12 }}>
        {titulo}
      </Title>
      {children}
    </section>
  );
}

export function rotinasQuePerdem(rotinas, filiaisMarcadas, nomes) {
  const marcadas = new Set(filiaisMarcadas);
  return (rotinas || [])
    .map((rotina) => {
      const ids = rotina.filial_ids || [];
      const perdidas = ids.filter((id) => !marcadas.has(id));
      return {
        nome: rotina.name,
        perdidas: perdidas.map((id) => nomes[id] || id),
        semNenhuma: ids.length > 0 && perdidas.length === ids.length,
      };
    })
    .filter((rotina) => rotina.perdidas.length);
}

function valoresIniciais(contato, filiaisIniciais) {
  if (contato) {
    return {
      nome: contato.nome,
      telefone: formatarTelefone(contato.telefone),
      usuario: contato.usuario ?? undefined,
      ativo: contato.ativo !== false,
      whatsapp: Boolean(contato.whatsapp),
      pode_escrever: Boolean(contato.pode_escrever),
      branch: contato.branch ?? undefined,
      filiais: contato.filiais_pelo_usuario ? [] : contato.filiais || [],
    };
  }
  return {
    nome: "",
    telefone: "",
    usuario: undefined,
    ativo: true,
    whatsapp: false,
    pode_escrever: false,
    branch: undefined,
    filiais: filiaisIniciais || [],
  };
}

export default function PainelDoContato({
  aberto,
  contato,
  cliente,
  filiais,
  contas,
  filiaisIniciais,
  contexto,
  aviso,
  salvarRotulo,
  compacto,
  comMascara,
  onFechar,
  onSalvo,
  onExcluido,
}) {
  const { createAgentPhone, updateAgentPhone, deleteAgentPhone } = useApi();
  const [form] = Form.useForm();
  const [salvando, setSalvando] = useState(false);
  const [alternando, setAlternando] = useState(false);
  const [excluindo, setExcluindo] = useState(false);
  const [conversaAberta, setConversaAberta] = useState(null);
  const conversa = Form.useWatch("whatsapp", form);
  const filialDoAgente = Form.useWatch("branch", form);
  const usuarioEscolhido = Form.useWatch("usuario", form);
  const filiaisMarcadas = Form.useWatch("filiais", form);
  const novo = !contato;
  const contasDoCliente = (contas || []).filter((conta) =>
    conta.client_ids.includes(cliente?.id)
  );
  const conta = contasDoCliente.find((item) => item.id === usuarioEscolhido);
  const idsDoCliente = new Set(filiais.map((f) => f.id));
  const nomes = Object.fromEntries(filiais.map((f) => [f.id, f.name]));
  const filiaisDoContato = conta
    ? conta.filiais.filter((id) => idsDoCliente.has(id))
    : usuarioEscolhido && contato?.usuario === usuarioEscolhido
      ? contato.filiais || []
      : usuarioEscolhido
        ? []
        : filiaisMarcadas || [];
  const opcoesDoAgente = filiais.filter((f) => filiaisDoContato.includes(f.id));
  const chaveDasOpcoes = opcoesDoAgente.map((f) => f.id).join(",");
  const perdas =
    !novo && !usuarioEscolhido
      ? rotinasQuePerdem(contato.rotinas, filiaisMarcadas || [], nomes)
      : [];
  const motivo = novo ? "" : motivoParaNaoExcluir(contato);
  const chaveDoContato = contato ? contato.id : "novo";

  useEffect(() => {
    if (aberto) {
      form.resetFields();
    }
  }, [aberto, chaveDoContato, form]);

  useEffect(() => {
    if (
      filialDoAgente &&
      !chaveDasOpcoes.split(",").includes(String(filialDoAgente))
    ) {
      form.setFieldValue("branch", undefined);
    }
  }, [chaveDasOpcoes, filialDoAgente, form]);

  const salvar = async (brutos) => {
    const filial = brutos.whatsapp
      ? brutos.branch
      : contato
        ? form.getFieldValue("branch")
        : null;
    const valores = {
      ...brutos,
      usuario: brutos.usuario ?? null,
      filiais: brutos.usuario ? [] : brutos.filiais || [],
      whatsapp: Boolean(brutos.whatsapp),
      pode_escrever: Boolean(brutos.whatsapp && brutos.pode_escrever),
      branch: filiaisDoContato.includes(filial) ? filial : null,
    };
    setSalvando(true);
    try {
      const salvo = contato
        ? await updateAgentPhone(contato.id, valores)
        : await createAgentPhone({ ...valores, client: cliente.id });
      onSalvo(salvo, contato ? "Contato salvo." : "Contato criado.");
    } catch (err) {
      notification.error({
        message: "Erro ao salvar o contato.",
        description: describeError(err, ""),
      });
    } finally {
      setSalvando(false);
    }
  };

  const alternarAtivo = async () => {
    setAlternando(true);
    try {
      const salvo = await updateAgentPhone(contato.id, {
        ativo: !contato.ativo,
      });
      onSalvo(
        salvo,
        contato.ativo ? "Contato inativado." : "Contato reativado."
      );
    } catch (err) {
      notification.error({
        message: "Erro ao alterar o contato.",
        description: describeError(err, ""),
      });
    } finally {
      setAlternando(false);
    }
  };

  const excluir = async () => {
    setExcluindo(true);
    try {
      await deleteAgentPhone(contato.id);
      onExcluido(contato);
    } catch (err) {
      notification.error({
        message: "Erro ao excluir o contato.",
        description: describeError(err, ""),
      });
    } finally {
      setExcluindo(false);
    }
  };

  const rodape = novo ? (
    <Space wrap>
      <Button type="primary" loading={salvando} onClick={() => form.submit()}>
        {salvarRotulo || "Salvar"}
      </Button>
      <Button onClick={onFechar}>Cancelar</Button>
    </Space>
  ) : (
    <div>
      <Space wrap>
        <Button type="primary" loading={salvando} onClick={() => form.submit()}>
          Salvar
        </Button>
        <Button loading={alternando} onClick={alternarAtivo}>
          {contato.ativo ? "Inativar" : "Reativar"}
        </Button>
        <Popconfirm
          title="Excluir este contato do cadastro do cliente?"
          okText="Excluir"
          cancelText="Cancelar"
          disabled={Boolean(motivo)}
          onConfirm={excluir}>
          <Button danger disabled={Boolean(motivo)} loading={excluindo}>
            Excluir
          </Button>
        </Popconfirm>
      </Space>
      {motivo && (
        <Text
          type="secondary"
          style={{ display: "block", marginTop: 8, fontSize: 13 }}>
          {motivo}
        </Text>
      )}
    </div>
  );

  return (
    <Drawer
      open={aberto}
      width={480}
      zIndex={1002}
      mask={Boolean(comMascara)}
      onClose={onFechar}
      destroyOnClose
      title={
        <div>
          <div>{novo ? "Novo contato" : contato.nome}</div>
          {contexto && (
            <Text type="secondary" style={{ fontSize: 13, fontWeight: 400 }}>
              {contexto}
            </Text>
          )}
        </div>
      }
      footer={rodape}
      styles={{ body: { paddingTop: 4 } }}>
      {aviso && (
        <Alert type="info" showIcon style={{ marginTop: 12 }} message={aviso} />
      )}
      <Form
        key={chaveDoContato}
        form={form}
        initialValues={valoresIniciais(contato, filiaisIniciais)}
        layout="vertical"
        requiredMark={false}
        onFinish={salvar}>
        <Secao titulo="Quem é">
          <Form.Item
            name="nome"
            label="Nome"
            rules={[{ required: true, message: "Informe o nome." }]}>
            <Input placeholder="Ex.: Ana, gerente da Loja 1" maxLength={120} />
          </Form.Item>
          <Form.Item
            name="telefone"
            label="Telefone"
            extra="DDD e número. O 55 é opcional."
            rules={[{ required: true, message: "Informe o telefone." }]}>
            <Input placeholder="(11) 91234-5678" maxLength={30} />
          </Form.Item>
          <Form.Item
            name="usuario"
            label="Usuário do app"
            extra="Com usuário, as filiais do contato são as dele no app.">
            <Select
              showSearch
              allowClear
              optionFilterProp="label"
              placeholder="Sem usuário"
              options={contasDoCliente.map((item) => ({
                value: item.id,
                label: item.nome ? `${item.nome} (${item.email})` : item.email,
              }))}
            />
          </Form.Item>
          <Form.Item>
            <Space size={8}>
              <Form.Item name="ativo" valuePropName="checked" noStyle>
                <Switch aria-label="Contato ativo" />
              </Form.Item>
              <Text>Contato ativo</Text>
            </Space>
          </Form.Item>
        </Secao>

        <Secao titulo="Filiais do contato">
          {usuarioEscolhido ? (
            filiaisDoContato.length ? (
              <Form.Item extra="Vêm do usuário no app, lidas na hora. Se ele sair de uma filial ou for inativado, o contato deixa de receber dela. Para mudar, altere o usuário no Painel de Controle.">
                <Space size={[6, 6]} wrap>
                  {filiaisDoContato.map((id) => (
                    <Tag key={id}>{nomes[id] || id}</Tag>
                  ))}
                </Space>
              </Form.Item>
            ) : (
              <Alert
                type="error"
                style={{ marginBottom: 16 }}
                message="O usuário não tem filial deste cliente. Sem filial, não recebe relatório nem conversa com o agente."
              />
            )
          ) : (
            <Form.Item
              name="filiais"
              rules={[
                {
                  required: true,
                  type: "array",
                  min: 1,
                  message:
                    "Marque ao menos uma filial. Sem filial, o contato não recebe nada.",
                },
              ]}>
              <Checkbox.Group
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(130px, 1fr))",
                  gap: "6px 12px",
                  maxHeight: 220,
                  overflowY: "auto",
                }}>
                {filiais.map((f) => (
                  <Checkbox key={f.id} value={f.id}>
                    {f.name}
                  </Checkbox>
                ))}
              </Checkbox.Group>
            </Form.Item>
          )}
          {perdas.map((perda) => (
            <Alert
              key={perda.nome}
              type={perda.semNenhuma ? "error" : "warning"}
              style={{ marginBottom: 12 }}
              message={
                perda.semNenhuma
                  ? `Ficaria sem filial na rotina ${perda.nome}. Tire-o da rotina antes.`
                  : `Ao salvar, deixa de receber ${perda.nome} de ${perda.perdidas.join(", ")}.`
              }
            />
          ))}
        </Secao>

        {!compacto && (
          <Secao titulo="Relatórios que recebe">
            {contato?.rotinas?.length ? (
              <>
                {contato.rotinas.map((rotina) => (
                  <div
                    key={rotina.id}
                    style={{
                      display: "flex",
                      gap: 12,
                      alignItems: "baseline",
                      padding: "6px 0",
                      borderBottom: "1px dashed #f0f0f0",
                    }}>
                    <div style={{ flex: 1 }}>
                      <Text strong>{rotina.name}</Text>
                      {rotina.ativa === false && (
                        <Tag style={{ marginLeft: 8 }}>Pausada</Tag>
                      )}
                      <Text
                        type="secondary"
                        style={{ display: "block", fontSize: 13 }}>
                        {(rotina.filial_ids || []).length === filiais.length &&
                        filiais.length > 1
                          ? "Todas as filiais"
                          : rotina.filiais.join(", ")}
                      </Text>
                    </div>
                    <a
                      href={`/ai_reports?rotina=${rotina.id}`}
                      target="_blank"
                      rel="noreferrer">
                      Abrir rotina
                    </a>
                  </div>
                ))}
                <Text
                  type="secondary"
                  style={{ display: "block", margin: "8px 0", fontSize: 13 }}>
                  Quem recebe cada relatório se decide na rotina.
                </Text>
              </>
            ) : (
              <Text
                type="secondary"
                style={{ display: "block", marginBottom: 12 }}>
                Não está inscrito em nenhuma rotina. A inscrição se faz na
                rotina, no passo Quem recebe.
              </Text>
            )}
          </Secao>
        )}

        <Secao titulo="Conversa com o agente" ultima={compacto || novo}>
          <Form.Item
            extra={
              conversa
                ? undefined
                : "Desligado, o contato só recebe relatórios."
            }>
            <Space size={8}>
              <Form.Item name="whatsapp" valuePropName="checked" noStyle>
                <Switch
                  aria-label="Conversa com o agente"
                  disabled={!filiaisDoContato.length && !conversa}
                />
              </Form.Item>
              <Text>Conversa com o agente pelo WhatsApp</Text>
            </Space>
          </Form.Item>
          {conversa && (
            <>
              <Form.Item
                name="branch"
                label="Agente em"
                extra="O agente só vê e registra dados desta filial."
                rules={[
                  { required: true, message: "Escolha a filial do agente." },
                ]}>
                <Select
                  showSearch
                  optionFilterProp="label"
                  placeholder="Escolha a filial"
                  options={opcoesDoAgente.map((f) => ({
                    value: f.id,
                    label: f.name,
                  }))}
                />
              </Form.Item>
              <Form.Item extra="Ligado, grava o que o contato pedir, sempre com confirmação. Desligado, só consulta.">
                <Space size={8}>
                  <Form.Item
                    name="pode_escrever"
                    valuePropName="checked"
                    noStyle>
                    <Switch aria-label="Pode registrar" />
                  </Form.Item>
                  <Text>Pode registrar</Text>
                </Space>
              </Form.Item>
              {contato?.agente_em_outro_cliente && (
                <Alert
                  type="error"
                  style={{ marginBottom: 16 }}
                  message={`Este número já conversa com o agente pela ${contato.agente_em_outro_cliente}. Um número fala com o agente por um cliente só. Desligue a conversa na ${contato.agente_em_outro_cliente} antes de ligar aqui.`}
                />
              )}
            </>
          )}
        </Secao>

        {!compacto && !novo && (
          <Secao titulo="Atividade" ultima>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "130px 1fr",
                gap: "4px 12px",
                fontSize: 13,
                marginBottom: 12,
              }}>
              <Text type="secondary">Último relatório</Text>
              <Text>
                {contato.ultima_entrega
                  ? formatDateTime(contato.ultima_entrega)
                  : "-"}
              </Text>
              <Text type="secondary">Última conversa</Text>
              <span>
                {contato.ultima_conversa ? (
                  <>
                    {formatDateTime(contato.ultima_conversa)}
                    {contato.ultima_conversa_id && (
                      <Button
                        type="link"
                        size="small"
                        onClick={() =>
                          setConversaAberta(contato.ultima_conversa_id)
                        }>
                        Abrir conversa
                      </Button>
                    )}
                  </>
                ) : (
                  "-"
                )}
              </span>
            </div>
          </Secao>
        )}
      </Form>
      {conversaAberta !== null && (
        <ConversationModal
          zIndex={1010}
          conversationId={conversaAberta}
          onClose={() => setConversaAberta(null)}
        />
      )}
    </Drawer>
  );
}
